// APP FORMS in an AI answer (U3; Nika 2026-10-01: "chatbot should be using
// all powerful functionalities from all apps ... reusing ui and
// functionalities right in the chatbot").
//
// The assistant PROPOSES a record (a new deal, an event moved to Friday, a
// task set to Blocked and given to Ana). The answer shows the proposal and ONE
// action that opens the APP'S OWN form, prefilled. Nothing is written until
// the member saves in that form — the form IS the ask. The app's own save
// function writes (SDK), then reports the saved record, and the answer shows
// it as a row that opens it.
//
// Two declarers, ONE shape (the views' — functions/assistantAppViews.js):
//   an installed app — manifest v2 `forms[]`
//   a built-in package — a `<pkg>AiForms()` provider in its EAGER slice that
//     returns `{ app, forms }` (calendarAiForms, ticketsAiForms)
//
//   {
//     id: 'crm-deal',              /^[a-z0-9-]+$/
//     title, describe              as a view (describe = the ONE line the
//                                  model matches a request against)
//     fields: { <key>: { type, values?, valuesFrom?, label, describe? } }
//                                  what the assistant may propose; the card
//                                  shows these, labelled, in this order
//     open: '<fn>'                 the app's function that seeds its form and
//                                  opens it: (formId, { values, recordId,
//                                  requestId }) — recordId null = create
//     component: '<Component>'     OPTIONAL — a sheet the shell's form host
//                                  mounts first (HomeWidgetFormHost, under the
//                                  app's context), for a form that is not
//                                  already mounted by its package
//     access: { admin?, permission?, collections? }   the views' rule
//   }
//
// WHERE `open` RUNS. For an installed app's form with a `component`, the host
// runs `open` ON the mounted sheet — the app's own function scope and its own
// state scope, the one the sheet reads (functions/moduleStateScope.js). Run
// on the card, a shell surface, the seed landed on the SHELL root while the
// sheet read the app's store: the app's own close (a module-local `draft:
// null`) then shadowed every later seed, and the sheet opened once per page
// load (WS-ASSISTANT-APP-FORM-OPEN-SHELL-SCOPE-1). A built-in package's form,
// and an app form with no `component`, are opened by their own code: `open`
// runs on the card, as before.
//
// THE REPORT. The app's save carries the `requestId` its form was opened with
// and, once the write succeeded, calls
//   el.call('assistantFormSaved', requestId, { id, title, meta?, route? })
// The card then shows that row; `route` opens it in the split.
//
// Frank note: el.call serializes each export on its own (FA204) — every export
// is self-contained.

// Every source of forms: the takeover module and each installed app
// (`forms[]`), then the built-in providers. Each: { app, forms }.
export const assistantAppFormSources = function assistantAppFormSources(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || {}
  const out = []
  const wp = r.workspaceProject
  if (wp && Array.isArray(wp.forms)) out.push({ app: wp, forms: wp.forms, builtin: false })
  for (const app of Array.isArray(r.workspaceApps) ? r.workspaceApps : []) {
    if (app && Array.isArray(app.forms)) out.push({ app, forms: app.forms, builtin: false })
  }
  const PROVIDERS = ['calendarAiForms', 'ticketsAiForms']
  const fns = (el && el.context && el.context.functions) || null
  for (const name of PROVIDERS) {
    if (fns && typeof fns[name] !== 'function') continue
    let got = null
    try {
      got = el.call(name)
    } catch (_) {
      got = null
    }
    if (!got || typeof got !== 'object' || !got.app || !Array.isArray(got.forms)) continue
    out.push({ app: got.app, forms: got.forms, builtin: true })
  }
  return out
}

// A form card → { status, form, appId } — status 'ready' | 'denied' |
// 'refused' | 'loading'; null for any other card. `form` is matched by
// "<app>.<id>" (app = the app id, its key or its short name) or a bare id.
export const assistantFormEntry = function assistantFormEntry(s) {
  const el = this
  const x = s && typeof s === 'object' ? s : {}
  if (x.type !== 'form') return null
  const root = el.state && el.state.root
  if (!root) return { status: 'loading', form: null, appId: null }
  const ref = typeof x.form === 'string' ? x.form : ''
  const dot = ref.indexOf('.')
  const app = dot > 0 ? ref.slice(0, dot) : ''
  const id = dot > 0 ? ref.slice(dot + 1) : ref
  const names = (appId) => {
    const full = String(appId || '')
    const key = full.split('/').pop()
    return !app || full === app || key === app || key.replace(/-app$/, '') === app
  }
  const sources = el.call('assistantAppFormSources', root)
  for (const src of Array.isArray(sources) ? sources : []) {
    const appId = src && src.app && src.app.id ? String(src.app.id) : ''
    if (!appId || !names(appId)) continue
    const form = src.forms.find((f) => f && f.id === id && typeof f.open === 'string')
    if (!form) continue
    if (!el.call('assistantAppViewAllowed', form.access, root)) {
      return { status: 'denied', form: null, appId }
    }
    return { status: 'ready', form, appId, builtin: !!src.builtin }
  }
  // An installed app that has not published yet: its form may still come.
  const settings = (root.workspace && root.workspace.settings) || {}
  const refs = el.call('installedAppRefs', settings)
  if (Array.isArray(refs) && refs.length && !Array.isArray(root.workspaceApps)) {
    return { status: 'loading', form: null, appId: null }
  }
  return { status: 'refused', form: null, appId: null }
}

export const assistantFormStatus = function assistantFormStatus(s) {
  const entry = this.call('assistantFormEntry', s)
  return entry ? entry.status : null
}

// The one quiet line a form card says instead of its body.
export const assistantFormNote = function assistantFormNote(s) {
  const status = this.call('assistantFormStatus', s)
  if (status === 'denied')
    return String(this.call('polyglot', 'workspace.assistantBlock.formDenied'))
  if (status === 'refused')
    return String(this.call('polyglot', 'workspace.assistantBlock.formGone'))
  return ''
}

// The proposal: the declared fields the card carries a value for, labelled,
// in the form's order. A key the form does not declare is never shown (nor
// passed to the app).
export const assistantFormFields = function assistantFormFields(s) {
  const entry = this.call('assistantFormEntry', s)
  if (!entry || entry.status !== 'ready') return []
  const decl = entry.form.fields && typeof entry.form.fields === 'object' ? entry.form.fields : {}
  const values = s.values && typeof s.values === 'object' ? s.values : {}
  const out = []
  for (const key of Object.keys(decl)) {
    const v = values[key]
    if (v == null || v === '') continue
    const d = decl[key] || {}
    const label = d.label ? String(this.call('polyglot', d.label)) : key
    // The app's own word for a value: `valueLabel` (the app's function — the
    // CRM's crmStageLabel names the WORKSPACE pipeline's stages) first, then
    // `labels` (the key the server reads, params.<name>.labels: per value a
    // bundle key or literal text, or an { en, ka } pair); else the value.
    const word = (one) => {
      const raw = String(one)
      if (typeof d.valueLabel === 'string' && d.valueLabel) {
        try {
          const t = this.call(d.valueLabel, raw)
          if (typeof t === 'string' && t.trim()) return t
        } catch (_) {
          /* the app's word is best-effort */
        }
      }
      const map = d.labels && typeof d.labels === 'object' ? d.labels : null
      const entry = map && Object.prototype.hasOwnProperty.call(map, raw) ? map[raw] : null
      if (typeof entry === 'string' && entry) {
        const t = this.call('polyglot', entry)
        return t ? String(t) : entry
      }
      if (entry && typeof entry === 'object') {
        const lang = String(this.call('getActiveLang') || 'en').split('-')[0]
        const t = entry[lang] || entry.en
        if (t) return String(t)
      }
      return raw
    }
    const text = Array.isArray(v)
      ? v.map(word).join(', ')
      : typeof v === 'object'
        ? JSON.stringify(v)
        : word(v)
    out.push({ key, label, value: text })
  }
  return out
}

// The request id: stable for the same proposal (form + values + record).
export const assistantFormRequestId = function assistantFormRequestId(s) {
  const x = s && typeof s === 'object' ? s : {}
  return (
    'frm_' +
    this.call('boardSpecHash', {
      form: x.form || '',
      values: x.values || {},
      recordId: x.recordId || null
    })
  )
}

// THE SAVED ROWS OUTLIVE A RELOAD: a report also lands in the browser's
// storage, per workspace (the creditGuard idiom — a defensive accessor, every
// read and write best-effort, never throws), so a reload or a second tab shows
// the saved row instead of offering the same write again. The 50 newest.
// `write` (optional) = { [requestId]: saved } to merge; returns the stored map.
export const assistantFormSavedStore = function assistantFormSavedStore(write) {
  const root = this.state && this.state.root
  const ws = root && root.workspace && (root.workspace.id || root.workspace._id)
  if (!ws) return {}
  const key = 'ai_forms_' + String(ws)
  let store = null
  try {
    store = globalThis.localStorage || null
  } catch (_) {
    store = null
  }
  if (!store) return {}
  let map = {}
  try {
    const raw = store.getItem(key)
    const parsed = raw ? JSON.parse(raw) : null
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) map = parsed
  } catch (_) {
    map = {}
  }
  if (write && typeof write === 'object') {
    map = { ...map, ...write }
    const keys = Object.keys(map)
    if (keys.length > 50) for (const k of keys.slice(0, keys.length - 50)) delete map[k]
    try {
      store.setItem(key, JSON.stringify(map))
    } catch (_) {
      /* storage full or denied: memory still has it */
    }
  }
  return map
}

// This card's request: { status: 'open' | 'saved', saved? } or null — this
// session's state first, then a row a past session saved.
export const assistantFormState = function assistantFormState(s) {
  const root = this.state && this.state.root
  const bag = root && root._aiForms && typeof root._aiForms === 'object' ? root._aiForms : {}
  const id = this.call('assistantFormRequestId', s)
  if (bag[id]) return bag[id]
  const stored = this.call('assistantFormSavedStore')
  const saved = stored && stored[id]
  return saved && typeof saved === 'object' ? { status: 'saved', saved } : null
}

// OPEN the app's own form, prefilled. The form host mounts the sheet first
// when the form names one; then the app's `open` seeds and shows it — run by
// the host on the mounted sheet for an installed app (see WHERE `open` RUNS
// above), on the card otherwise. Only the declared fields travel. Returns the
// request id (null when not ready).
export const assistantFormOpen = function assistantFormOpen(s) {
  const el = this
  const entry = el.call('assistantFormEntry', s)
  const root = el.state && el.state.root
  if (!entry || entry.status !== 'ready' || !root || typeof root.update !== 'function') return null
  const form = entry.form
  const decl = form.fields && typeof form.fields === 'object' ? form.fields : {}
  const given = s.values && typeof s.values === 'object' ? s.values : {}
  const values = {}
  for (const key of Object.keys(decl)) if (given[key] !== undefined) values[key] = given[key]
  const requestId = el.call('assistantFormRequestId', s)
  const bag = root._aiForms && typeof root._aiForms === 'object' ? root._aiForms : {}
  const prev = bag[requestId]
  if (!(prev && prev.status === 'saved')) {
    root.update({ _aiForms: { ...bag, [requestId]: { status: 'open' } } }, { preventFetch: true })
  }
  const args = [form.id, { values, recordId: s.recordId || null, requestId }]
  try {
    if (typeof form.component === 'string' && form.component) {
      const hosted = !entry.builtin
      el.call('openHomeWidgetForm', {
        form: form.component,
        moduleId: entry.appId,
        widgetId: form.id,
        ...(hosted ? { open: { call: form.open, args } } : {})
      })
      if (hosted) return requestId
    }
    el.call(form.open, ...args)
  } catch (err) {
    console.warn('[assistantAppForms] open failed:', form.id, err && err.message)
    return null
  }
  return requestId
}

// A STARTER THAT NAMES AN APP'S FORM (boards-r1 follow-up b; BLD-26 — the
// CRM's "Add a contact" starter made a page whose "Do it" ran a paid Tools
// turn, while the CRM ships its own contact form). A manifest starter
// `{ text, form: '<form id>' }` becomes a row `{ localAction:
// 'assistantStarterForm', form: '<app id>.<form id>' }` (assistantStarters.js
// moduleStarters, aiPanel.js); pressing it opens that form here, locally and
// empty — no page, no assistant turn. The row rides as the argument, or as
// the pressed element's own state (the shared AssistantStarter calls a
// local action with no argument). A form this workspace cannot open asks the
// starter's words instead, as any starter.
export const assistantStarterForm = function assistantStarterForm(row) {
  const st = row && typeof row === 'object' ? row : this.state
  const x = st && typeof st.parse === 'function' ? st.parse() : st
  const ref = x && typeof x.form === 'string' ? x.form.trim() : ''
  const card = ref ? { type: 'form', form: ref, values: {} } : null
  if (card && this.call('assistantFormStatus', card) === 'ready') {
    return this.call('assistantFormOpen', card)
  }
  const prompt = String((x && (x.prompt || x.text)) || '').trim()
  const fns = (this.context && this.context.functions) || {}
  if (prompt && typeof fns.homeAiSend === 'function') return this.call('homeAiSend', prompt)
  return null
}

// THE REPORT (the app's save calls it): the request becomes 'saved' with the
// saved row { id, title, meta?, route? }. An unknown request changes nothing.
export const assistantFormSaved = function assistantFormSaved(requestId, saved) {
  const root = this.state && this.state.root
  const bag = root && root._aiForms && typeof root._aiForms === 'object' ? root._aiForms : null
  if (!bag || !requestId || !bag[requestId] || typeof root.update !== 'function') return false
  const row = saved && typeof saved === 'object' ? saved : {}
  const clean = {
    id: row.id != null ? String(row.id) : '',
    title: row.title ? String(row.title) : '',
    meta: row.meta ? String(row.meta) : '',
    route: typeof row.route === 'string' && row.route.startsWith('/') ? row.route : null
  }
  root.update(
    { _aiForms: { ...bag, [requestId]: { status: 'saved', saved: clean } } },
    { preventFetch: true }
  )
  try {
    this.call('assistantFormSavedStore', { [requestId]: clean })
  } catch (_) {
    /* best-effort: the session keeps it */
  }
  return true
}

// The saved row (the home kit's row): title, meta, and it opens its record.
export const assistantFormSavedRows = function assistantFormSavedRows(s) {
  const state = this.call('assistantFormState', s)
  if (!state || state.status !== 'saved' || !state.saved) return []
  const r = state.saved
  return [
    {
      key: r.id || 'saved',
      title: r.title || String(this.call('polyglot', 'workspace.assistantBlock.formSaved')),
      meta: r.meta,
      icon: 'check',
      ...(r.route ? { open: { call: 'assistantFormOpenSaved', arg: r.route } } : {})
    }
  ]
}

export const assistantFormOpenSaved = function assistantFormOpenSaved(route) {
  if (typeof route !== 'string' || !route.startsWith('/')) return false
  this.call('openInSlot', route, this)
  return true
}

// The card's action word: open the form, open it again, or none once saved.
export const assistantFormAction = function assistantFormAction(s) {
  const state = this.call('assistantFormState', s)
  if (state && state.status === 'saved') return ''
  return String(
    this.call(
      'polyglot',
      state && state.status === 'open'
        ? 'workspace.assistantBlock.formReopen'
        : 'workspace.assistantBlock.formReview'
    )
  )
}

// The line under the proposal: what happens next, or that it is saved.
export const assistantFormStatusText = function assistantFormStatusText(s) {
  const state = this.call('assistantFormState', s)
  return String(
    this.call(
      'polyglot',
      state && state.status === 'saved'
        ? 'workspace.assistantBlock.formSaved'
        : 'workspace.assistantBlock.formHint'
    )
  )
}
